import { FC, useCallback } from "react"
import GlobalDefaultsEditor, { GlobalDefaults } from "./global-defaults-editor"

interface GeneralSettings {
  cache_ttl?: number
  target_price?: number
  prepaid_interest_days?: number
  default_fees_in?: boolean
  fee_engine?: string
  waive_escrows?: boolean
  disable?: boolean
}

interface Props {
  currentSettings: GeneralSettings
  onChange: (settings: GeneralSettings) => void
  currentGlobalDefaults: GlobalDefaults
  onGlobalDefaultsChange: (defaults: GlobalDefaults) => void
}

const GeneralSettingsManager: FC<Props> = ({
  currentSettings,
  onChange,
  currentGlobalDefaults,
  onGlobalDefaultsChange
}) => {
  const handleCacheTtlChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      onChange({
        ...currentSettings,
        cache_ttl: value === "" ? undefined : Number(value)
      })
    },
    [currentSettings, onChange]
  )

  const handleTargetPriceChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      onChange({
        ...currentSettings,
        target_price: value === "" ? undefined : Number(value)
      })
    },
    [currentSettings, onChange]
  )

  const handlePrepaidInterestDaysChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      onChange({
        ...currentSettings,
        prepaid_interest_days: value === "" ? undefined : Number(value)
      })
    },
    [currentSettings, onChange]
  )

  const handleDefaultFeesInChange = useCallback(
    (checked: boolean) => {
      onChange({
        ...currentSettings,
        default_fees_in: checked
      })
    },
    [currentSettings, onChange]
  )

  const handleFeeEngineChange = useCallback(
    (checked: boolean) => {
      onChange({
        ...currentSettings,
        fee_engine: checked ? "lodestar" : undefined
      })
    },
    [currentSettings, onChange]
  )

  const handleWaiveEscrowsChange = useCallback(
    (checked: boolean) => {
      onChange({
        ...currentSettings,
        waive_escrows: checked
      })
    },
    [currentSettings, onChange]
  )

  const handleDisableChange = useCallback(
    (checked: boolean) => {
      onChange({
        ...currentSettings,
        disable: checked
      })
    },
    [currentSettings, onChange]
  )

  const inputClassName =
    "block w-full px-3 py-2 border border-neutral-200 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 rounded-lg p-4">
        <p className="text-sm text-gray-600 mb-4">
          Configure general pricing engine settings for this rateflow.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cache TTL (hours)
            </label>
            <input
              type="number"
              min="0"
              step="1"
              value={currentSettings.cache_ttl ?? ""}
              onChange={handleCacheTtlChange}
              placeholder="1"
              className={inputClassName}
            />
            <p className="text-xs text-gray-500 mt-1">
              How long to cache pricing results. Default is 1 hour.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Price
            </label>
            <input
              type="number"
              step="0.001"
              value={currentSettings.target_price ?? ""}
              onChange={handleTargetPriceChange}
              placeholder="e.g. 100.125"
              className={inputClassName}
            />
            <p className="text-xs text-gray-500 mt-1">
              Target price point for pricing calculations. Leave empty for
              default.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Prepaid Interest Days
            </label>
            <input
              type="number"
              min="0"
              step="1"
              value={currentSettings.prepaid_interest_days ?? ""}
              onChange={handlePrepaidInterestDaysChange}
              placeholder="1"
              className={inputClassName}
            />
            <p className="text-xs text-gray-500 mt-1">
              Number of prepaid interest days to include in pricing.
            </p>
          </div>
        </div>

        {/* Toggle Settings */}
        <div className="border-t border-gray-200 pt-4 mt-4 space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={currentSettings.default_fees_in ?? false}
              onChange={e => handleDefaultFeesInChange(e.target.checked)}
              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-700">
                Default Fees In
              </span>
              <p className="text-xs text-gray-500">
                Include fees in pricing calculations by default.
              </p>
            </div>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={currentSettings.fee_engine === "lodestar"}
              onChange={e => handleFeeEngineChange(e.target.checked)}
              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-700">
                Enable Lodestar Fee Engine
              </span>
              <p className="text-xs text-gray-500">
                Use Lodestar for fee calculations.
              </p>
            </div>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={currentSettings.waive_escrows ?? false}
              onChange={e => handleWaiveEscrowsChange(e.target.checked)}
              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-700">
                Waive Escrows
              </span>
              <p className="text-xs text-gray-500">
                Waive escrow requirements for pricing calculations.
              </p>
            </div>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={currentSettings.disable ?? false}
              onChange={e => handleDisableChange(e.target.checked)}
              className="h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-700">
                Disable Pricing Engine
              </span>
              <p className="text-xs text-gray-500">
                Temporarily disable this pricing engine configuration.
              </p>
            </div>
          </label>
        </div>
      </div>

      {/* Global Request Defaults */}
      <GlobalDefaultsEditor
        currentDefaults={currentGlobalDefaults}
        onChange={onGlobalDefaultsChange}
      />
    </div>
  )
}

export default GeneralSettingsManager
